import { useMemo } from 'react'
import { Helmet } from 'react-helmet-async'

import { LabFeatureFlags } from 'constants/lab'
import { useAppSelector } from 'hooks/reduxHooks'
import { buildPublicOffersAbsoluteUrl, buildPublicOffersUrl } from 'api/offer'
import { isOfferDisplayFlavorPayload } from 'lib/lab/dynamicPayload'
import { getOfferDeepLink } from 'selectors/offerDeepLinkSelector'
import { getDefaultAirportCode } from 'selectors/flightsSelectors'
import { DEFAULT_LOCALE } from 'i18n/util/locale'

interface Props {
  ymalListKey: string
  /**
   * Number of YMAL offer IDs included in the head warm-up URL.
   *
   * MUST equal the responsive number of YMAL tiles that fire `useOffer`
   * immediately on hydration (one on mobile, three otherwise). Tiles beyond
   * that use `lazyLoad='offer-and-inview'` so they don't dispatch until
   * scrolled near. Those eager dispatches hit the 32ms
   * `getAccumulatedOfferById` accumulator and batch into one bulk fetch.
   */
  maxOfferIds: number
}

export function buildMetaSearchWarmupScript(warmupUrl: string, locale: string): string {
  const serialisedUrl = JSON.stringify(warmupUrl)
  const serialisedLocale = JSON.stringify(locale)

  return `
      (function() {
        window.__META_SEARCH_PUBLIC_OFFERS_WARMUP__ = window.__META_SEARCH_PUBLIC_OFFERS_WARMUP__ || {};
        if (window.__META_SEARCH_PUBLIC_OFFERS_WARMUP__[${serialisedUrl}]) return;
        window.__META_SEARCH_PUBLIC_OFFERS_WARMUP__[${serialisedUrl}] = fetch(${serialisedUrl}, {
          credentials: 'same-origin',
          headers: { 'Accept-Language': ${serialisedLocale} },
        })
          .then(function(response) {
            if (!response.ok) return undefined;
            return response.json();
          })
          .catch(function() {
            return undefined;
          });
      })();
    `
}

/**
 * Emits an inline head script that warms the YMAL bulk
 * `/api/v2/public-offers?offerIds=...` request and stores the pending promise
 * on `window.__META_SEARCH_PUBLIC_OFFERS_WARMUP__`.
 *
 * Runtime `getOffersById` reads this map and reuses the same promise/data, so
 * this path does not rely on strict browser cache matching.
 *
 * Correctness invariant: the warm-up URL MUST stay byte-identical to the
 * runtime URL shape. This is achieved by:
 *
 * 1. Reusing the shared `buildPublicOffersUrl` helper (also called by
 *    `getOffersById`).
 * 2. Reusing `buildPublicOffersAbsoluteUrl` to mirror the endpoint + `brand`
 *    augmentation that runtime fetches receive through `brandLoader`.
 * 3. Reading the same per-request selectors (`flightOrigin`, `offerDeepLink`,
 *    lab `flavor` payload) that `fetchAccumulatedOfferById` reads at fetch
 *    time.
 * 4. Forwarding the resolved app locale as `Accept-Language`, matching
 *    `localeLoader` for the runtime fetch.
 *
 * Flash slot 0 ("same property, better deal") is merged into the YMAL list
 * server-side via `fetchMetaOfferIds(filters, prependOfferId)` so this
 * component can stay flash-agnostic — it just reads whatever IDs sit in the
 * SSR-populated YMAL list.
 *
 * The warm-up URL is intentionally dateless: the runtime YMAL tile summary
 * fetch (`useOffer` → `fetchAccumulatedOfferById`) does not pass
 * `checkIn`/`checkOut`, so the warm-up URL must remain dateless to byte-match
 * it.
 */
function MetaSearchPageWarmupHints({ ymalListKey, maxOfferIds }: Props) {
  const region = useAppSelector((state) => state.geo.currentRegionCode)
  const locale = useAppSelector((state) => state.geo.locale)
  const flightOrigin = useAppSelector(getDefaultAirportCode)
  const offerDeepLink = useAppSelector(getOfferDeepLink)
  const isSpoofed = useAppSelector((state) => state.auth.account.isSpoofed)
  const offerDisplayFlavorFlag = useAppSelector(
    (state) => state.system.features.labFlags[LabFeatureFlags.enableOfferDisplayFlavor],
  )
  const ymalOfferIds = useAppSelector(
    (state) => state.offer.offerLists[ymalListKey]?.offerIds,
  )

  const flavor = offerDisplayFlavorFlag?.enabled && isOfferDisplayFlavorPayload(offerDisplayFlavorFlag.value)
    ? offerDisplayFlavorFlag.value.flavor
    : undefined

  const warmupUrl = useMemo(() => {
    if (offerDeepLink || isSpoofed) return undefined

    const offerIds = (ymalOfferIds ?? []).slice(0, maxOfferIds)
    if (offerIds.length === 0) return undefined

    const path = buildPublicOffersUrl(offerIds, {
      region,
      flightOrigin,
      offerDeepLink: undefined,
      flavor,
    })

    return buildPublicOffersAbsoluteUrl(path)
  }, [ymalOfferIds, region, flightOrigin, flavor, offerDeepLink, isSpoofed, maxOfferIds])

  const warmupScript = useMemo(() => {
    if (!warmupUrl) return undefined
    return buildMetaSearchWarmupScript(warmupUrl, locale ?? DEFAULT_LOCALE)
  }, [warmupUrl, locale])

  if (!warmupScript) return null

  return (
    <Helmet>
      <script>{warmupScript}</script>
    </Helmet>
  )
}

export default MetaSearchPageWarmupHints
